import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfo, faSpinner, faSync, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { DocumentService, sharedApiClient } from '../../fetcher';
import { useNotifications } from '@medprompt/shared';
import { formatContentType, getVisibleTileError, resolveTileErrorContentTypes } from './contentTypes';

export const RegenerateButton = React.memo(({ 
  entityId,
  documentId,  // backward compatibility alias for entityId
  contentType, 
  isEnabled, 
  onRegenerateClick,
  regenerateService  // optional: function(entityId, contentType) => Promise - defaults to DocumentService
}) => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const documentManager = useMemo(() => new DocumentService(sharedApiClient), []);
  const { addNotification } = useNotifications();
  
  const id = entityId || documentId;

  useEffect(() => {
    if (isEnabled) {
      setIsRegenerating(false);
    }
  }, [isEnabled]);

  const handleRegenerateClick = async () => {
    if (!isEnabled || isRegenerating) return;
    
    setIsRegenerating(true);
    const displayName = formatContentType(contentType);
    try {
      // Use provided regenerateService or default to DocumentService
      if (regenerateService) {
        await regenerateService(id, contentType);
      } else {
        await documentManager.regenerateDocumentContent(id, contentType);
      }
      addNotification(`regenerate-content-${id}-${contentType}`, `${displayName} began regenerating`);
      onRegenerateClick && onRegenerateClick();
    } catch (error) {
      console.error(`Error regenerating ${contentType}:`, error);
      addNotification(`regenerate-content-error-${id}-${contentType}`, `Failed to regenerate ${displayName}`, { isError: true });
    }
  };

  return (
    <button 
      className={`regenerate-button ${!isEnabled || isRegenerating ? 'disabled' : ''}`}
      onClick={handleRegenerateClick}
      disabled={!isEnabled || isRegenerating}
      title={contentType ? `Regenerate ${contentType.replace('_STATUS', '').toLowerCase()}` : undefined}
    >
      <FontAwesomeIcon icon={isRegenerating ? faSpinner : faSync} spin={isRegenerating} />
    </button>
  );
});

// Helper function to create section header with checkbox and optional info button
export const SectionHeader = (title, checkHandler, onInfoClick = null) => (
  <div className="suggestion-header">
    <div className="suggestion-header-left">
      <h4>{title}</h4>
      {onInfoClick && (
        <button 
          className="info-button" 
          onClick={onInfoClick}
          title="Search in document"
        >
          <FontAwesomeIcon icon={faInfo} />
        </button>
      )}
    </div>
    <input 
      type="checkbox" 
      className="code-checkbox" 
      onChange={checkHandler} 
    />
  </div>
);

// Reusable empty state message component
export const EmptyStateMessage = React.memo(({ message }) => (
  <div className="empty-state-container">
    <div className="empty-state-message">{message}</div>
  </div>
));

// Reusable tile header component with regenerate button and optional notification badge
// Supports both documents (documentId) and consultations (entityId) via regenerateService prop
export const TileHeader = React.memo(({ 
  entityId,
  documentId,  // backward compatibility alias for entityId
  contentType, 
  title, 
  isEnabled, 
  onRegenerateClick,
  regenerateService,  // optional: function(entityId, contentType) => Promise - defaults to DocumentService
  badgePredicate, 
  badgeValue, 
  children 
}) => (
  <div className="tile-header-container">
    <RegenerateButton 
      entityId={entityId || documentId} 
      contentType={contentType} 
      isEnabled={isEnabled} 
      onRegenerateClick={onRegenerateClick}
      regenerateService={regenerateService}
    />
    <h3 className="tile-header">
      {title}
      {badgePredicate && badgeValue && (
        <span className="tile-notification-badge">{badgeValue}</span>
      )}
    </h3>
    {children ? (
      <div className="header-controls">{children}</div>
    ) : null}
  </div>
));

// Loading component for sections that are still processing
export const LoadingSection = React.memo(() => (
  <div className="loading-container">
    <div className="loading-spinner">
      <div className="spinner-circle"></div>
    </div>
    <div className="loading-text">Processing content...</div>
  </div>
));

// Conditional content component to handle loading state
export const ConditionalContent = React.memo(({ condition, children, loadingComponent = <LoadingSection /> }) => (
  condition ? children : loadingComponent
));

export const TileErrorState = ({
  documentId,
  entityId,
  error,
  isRetryEnabled,
  onRegenerateClick,
  regenerateService,
  compact = false
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const documentManager = useMemo(() => new DocumentService(sharedApiClient), []);
  const { addNotification } = useNotifications();
  const id = entityId || documentId;
  const failedTypes = error?.contentTypes || [];

  useEffect(() => {
    if (isRetryEnabled) {
      setIsRetrying(false);
    }
  }, [isRetryEnabled]);

  const handleTryAgain = async () => {
    if (!isRetryEnabled || isRetrying || !id || failedTypes.length === 0) {
      return;
    }

    setIsRetrying(true);
    try {
      for (const contentType of failedTypes) {
        if (regenerateService) {
          await regenerateService(id, contentType);
        } else {
          await documentManager.regenerateDocumentContent(id, contentType);
        }
      }
      const displayName = formatContentType(failedTypes[0]);
      addNotification(`regenerate-content-${id}-${failedTypes[0]}`, `${displayName} began regenerating`);
      onRegenerateClick && onRegenerateClick();
    } catch (retryError) {
      console.error('Error retrying content generation:', retryError);
      addNotification(
        `regenerate-content-error-${id}-${failedTypes[0]}`,
        'Failed to retry this section',
        { isError: true }
      );
      setIsRetrying(false);
    }
  };

  return (
    <div className={`tile-error-state${compact ? ' compact' : ''}`}>
      <div className="tile-error-headline">
        <FontAwesomeIcon icon={faTriangleExclamation} className="tile-error-icon" />
        This section failed to generate
      </div>
      {error?.message && (
        <div className="tile-error-message">{error.message}</div>
      )}
      <button
        type="button"
        className="header-button"
        onClick={handleTryAgain}
        disabled={!isRetryEnabled || isRetrying}
        title={isRetryEnabled ? 'Try generating this section again' : 'Cannot retry in current state'}
      >
        <FontAwesomeIcon icon={isRetrying ? faSpinner : faSync} spin={isRetrying} />
        Try again
      </button>
    </div>
  );
};

export const ContentErrorBlock = ({
  doc,
  documentId,
  entityId,
  contentType,
  isRetryEnabled,
  onRegenerateClick,
  regenerateService,
  shouldShowError,
  compact = false
}) => {
  const error = getVisibleTileError(doc, contentType);
  if (!error || (shouldShowError && !shouldShowError(error))) {
    return null;
  }
  return (
    <TileErrorState
      documentId={documentId || doc?.documentId}
      entityId={entityId}
      error={error}
      isRetryEnabled={isRetryEnabled}
      onRegenerateClick={onRegenerateClick}
      regenerateService={regenerateService}
      compact={compact}
    />
  );
};

export const Tile = ({
  className,
  documentId,
  entityId,
  contentType,
  errorContentTypes,
  title,
  condition,
  isEnabled,
  onRegenerateClick,
  regenerateService,
  headerContent,
  preContent,
  badgePredicate,
  badgeValue,
  doc,
  shouldShowError,
  showContentOnError = false,
  children
}) => {
  const detectedError = getVisibleTileError(
    doc,
    resolveTileErrorContentTypes(contentType, errorContentTypes)
  );
  const visibleError = detectedError && (shouldShowError?.(detectedError) ?? true)
    ? detectedError
    : null;
  const showContent = !visibleError || showContentOnError;

  return (
    <div className={`tile ${className || ''}${visibleError ? ' tile-has-error' : ''}`.trim()}>
      <TileHeader
        entityId={entityId}
        documentId={documentId}
        contentType={contentType}
        title={title}
        isEnabled={isEnabled}
        onRegenerateClick={onRegenerateClick}
        regenerateService={regenerateService}
        badgePredicate={badgePredicate}
        badgeValue={badgeValue}
      >
        {headerContent}
      </TileHeader>
      {visibleError && (
        <TileErrorState
          documentId={documentId}
          entityId={entityId}
          error={visibleError}
          isRetryEnabled={isEnabled}
          onRegenerateClick={onRegenerateClick}
          regenerateService={regenerateService}
        />
      )}
      {showContent && (
        <>
          {preContent}
          <ConditionalContent condition={condition}>
            {children}
          </ConditionalContent>
        </>
      )}
    </div>
  );
};
